// Browser adapter around the pure recognition core (./core).
//
// Builds the reference set once from the shipped sprite atlas (sprites.webp,
// which carries per-icon alpha → silhouette) in pk order, then turns a cropped
// region of a photo into RGB planes the core can match. All heavy math lives in
// core.ts; this file only does DOM/canvas I/O and pk bookkeeping.

import { db } from '../db/client';
import { buildRef, recognize, type RefEntry, type RGB } from './core';

const TILE = 32;          // atlas tile size in px
const WORK = 48;          // verify working resolution (must match core default)
const PROBE = 192;        // resolution the cropped photo region is sampled at

interface Scanner {
  refs: RefEntry[];
  pkByIndex: number[];    // refs[i] ↔ item pk
}

let scanner: Scanner | null = null;
let building: Promise<Scanner> | null = null;

async function loadAtlasPixels(version: number | null): Promise<ImageData> {
  // Version query keeps the atlas in lock-step with the DB (same pk order) past
  // the SW CacheFirst TTL — otherwise a stale atlas would mis-map sprites.
  const img = new Image();
  img.src = version != null ? `/data/sprites.webp?v=${version}` : '/data/sprites.webp';
  await img.decode();
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(img, 0, 0);
  return ctx.getImageData(0, 0, c.width, c.height);
}

// Build (and cache) the reference set. Safe to call repeatedly.
export async function ensureScanner(): Promise<{ count: number }> {
  if (scanner) return { count: scanner.refs.length };
  if (!building) {
    building = (async () => {
      const info = await db().ready();
      const [meta, atlas] = await Promise.all([db().allRefs(), loadAtlasPixels(info.version)]);
      const dummyHash = new Uint8Array(8);
      const refs: RefEntry[] = [];
      const pkByIndex: number[] = [];
      const tile = new Uint8ClampedArray(TILE * TILE * 4);
      for (const m of meta) {
        // Комнаты — это иконки миникарты, не физические объекты: в распознавание
        // не берём (остаются только в справочнике).
        if (m.kind === 'room') continue;
        const ox = m.sprite_x * TILE, oy = m.sprite_y * TILE;
        let hasAlpha = false;
        for (let y = 0; y < TILE; y++) {
          const src = ((oy + y) * atlas.width + ox) * 4;
          tile.set(atlas.data.subarray(src, src + TILE * 4), y * TILE * 4);
          if (!hasAlpha) for (let x = 0; x < TILE; x++) if (tile[y * TILE * 4 + x * 4 + 3]! > 8) { hasAlpha = true; break; }
        }
        if (!hasAlpha) continue; // нет иконки (недоступное вложение) — пропускаем
        refs.push(buildRef(tile, dummyHash, WORK));
        pkByIndex.push(m.pk);
      }
      return { refs, pkByIndex };
    })();
  }
  scanner = await building;
  return { count: scanner.refs.length };
}

// Sample the user's square crop of `img` (relative [0..1] coords) into RGB
// planes at PROBE resolution.
function probeFromCrop(img: HTMLImageElement, sx: number, sy: number, side: number): RGB & { w: number; h: number } {
  const c = document.createElement('canvas');
  c.width = PROBE; c.height = PROBE;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.imageSmoothingEnabled = true;
  (ctx as CanvasRenderingContext2D & { imageSmoothingQuality?: ImageSmoothingQuality }).imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, side, side, 0, 0, PROBE, PROBE);
  const data = ctx.getImageData(0, 0, PROBE, PROBE).data;
  const n = PROBE * PROBE;
  const r = new Float32Array(n), g = new Float32Array(n), b = new Float32Array(n);
  for (let i = 0; i < n; i++) { r[i] = data[i * 4]!; g[i] = data[i * 4 + 1]!; b[i] = data[i * 4 + 2]!; }
  return { r, g, b, w: PROBE, h: PROBE };
}

export interface ScanMatch { pk: number; score: number; }

// Recognise the item inside the crop. `crop` is in natural-image pixels.
export async function recognisePhoto(
  img: HTMLImageElement,
  crop: { sx: number; sy: number; side: number },
  topN = 5,
): Promise<ScanMatch[]> {
  await ensureScanner();
  const s = scanner!;
  const probe = probeFromCrop(img, crop.sx, crop.sy, crop.side);
  const scored = recognize(probe, probe.w, probe.h, s.refs);
  return scored.slice(0, topN).map((c) => ({ pk: s.pkByIndex[c.idx]!, score: c.score }));
}
